/**
 * The session domain's FLAG MODULE (ruling 36 as amended 2026-08-29).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/session/flags` publishes both.
 * `/contributions` carries nothing for flags — this domain HAS such a door, and
 * none of the declarations below belongs in it.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from a declaration above it, so the pair cannot drift, and `flags.test.ts`
 * asserts each key appears exactly once in this file and that no read takes a
 * string literal — the only assertion that can tell a descriptor read from a
 * hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard of their own (cache keys do), so this file's source
 * case and `scripts/check-flag-getter-doors.test.ts` are the whole of what
 * stands between a key and a second spelling.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/session/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects. `isTauri` therefore comes from `@heidi/runtime/platform`, the door
 * beside the flags door, rather than from the app's `@/tauri/utils`.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/session/flags`, so its importers and their
 * `jest.mock` literals are untouched.
 *
 * WHAT DOES NOT BELONG HERE — TEN of this domain's twenty-five app-barrel names
 * are NOT below, and each is named rather than silently skipped:
 *
 *   - `useIsSessionPageV2Enabled` and `useIsSessionPageV2InviteEnabled` compose
 *     `useLabsFeatureOptIn` / `useIsAutoEnrolledSessionLayoutV2` from the app's
 *     heidi-labs overrides module, and `useIsLabsExperimentalTermsGateEnabled`
 *     gates that settings surface itself. Labs is app-shell.
 *   - `useIsOfflineRecordingEnabled` composes
 *     `useCachedOfflineRecordingFlagFallback`, an app module holding a
 *     localStorage cache the door has no equivalent for.
 *   - `useIsDesktopChronicleEnabled`, `useIsChronicleWifiSyncEnabled`,
 *     `useIsChronicleLocalFirmwareUpgradeEnabled` and
 *     `useIsReleaseRemoteSyncAsyncWebEnabled` gate chronicle and remote-sync
 *     surfaces, which are not session roots — their consumers are
 *     `src/features/chronicle` and `src/hooks/desktop`.
 *   - `useIsAmbientCopilotEnabled` belongs to the copilot root, which the
 *     2026-08-21 roots ruling puts app-shell.
 *   - `useIsSessionUrgencyEnabled` resolves through
 *     `useFeatureFlagEnabled` from `@/features/flags/posthog-compat`, which
 *     reads posthog-js on cloud and only falls back to OpenFeature on the
 *     airgapped build. `@heidi/runtime/feature-flags` carries no PostHog
 *     surface, so moving it would change WHICH PROVIDER answers — a behaviour
 *     change wearing a refactor's clothes, and
 *     `monorepo-wave-1-p3-feature-flags` Z4 is the standing rule against it.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
  useBooleanFlagLite,
} from '@heidi/runtime/feature-flags';
import { isTauri } from '@heidi/runtime/platform';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on twenty-one of these, measured rather than assumed. The surfaces
// these gate are the session list and its toolbar, the appointment list, the
// schedule-upload flow, session merge settings, the Scribe Canvas experience and
// the Context tab — and `rg isOnPrem` over `src/features/sessions`,
// `src/features/canvas`, `src/features/uploadSessionSchedule` and
// `src/features/session-merge-onboarding` returns nothing. Omitting the field
// would silently take the D6 default (`'cloud-only'`) and withhold the
// appliance's own session list from it.
// ---------------------------------------------------------------------------

/**
 * Gates the prominent session-list primary action and its supporting toolbar
 * and header layout. This rollout is independent from the Start Now project.
 */
export const sessionListPrimaryActionFlag = defineFeatureFlag({
  key: 'release-scribe-session-list-primary-action',
  defaultValue: false,
  description: 'Session-list primary action and supporting toolbar layout',
  builds: 'all',
});

/** The new session list — table view and consolidated session lists. */
export const newSessionListFlag = defineFeatureFlag({
  key: 'release-scribe-enable-new-session-list',
  defaultValue: false,
  description: 'New session list with table view and consolidated lists',
  builds: 'all',
});

/**
 * Whole-scope status counts for the All Sessions filter. Default off until the
 * scope-aware indexes are live in every region. The raw app read reached every
 * build, so this ships to the appliance too.
 */
export const multiScopeStatusCountsFlag = defineFeatureFlag({
  key: 'release-scribe-multi-scope-status-counts',
  defaultValue: false,
  description: 'Whole-scope status counts for the All Sessions list',
  builds: 'all',
});

/** Stable, unanimated startup sizing for the new session-list split. */
export const stableSessionSplitStartupFlag = defineFeatureFlag({
  key: 'release-scribe-enable-stable-session-split-startup',
  defaultValue: false,
  description: 'Stable initial session-list split sizing on web and desktop',
  builds: 'all',
});

/**
 * THE KEY, PUBLISHED — and derived rather than declared.
 *
 * The app barrel exported this constant before the move and five production
 * modules import it by name to stamp `experiment:` onto their tracking payloads,
 * so it cannot vanish. Re-declaring the literal in the barrel would be a SECOND
 * MINTING SITE, which is the one thing ruling 36 exists to refuse, and neither
 * side's own suite can see that: this file's mint-count case reads only this
 * source, and the app's suite asserts a VALUE, which a copy satisfies. What
 * refuses it is `scripts/check-flag-getter-doors.test.ts` ('a declared key is
 * minted ONCE, on both sides of the door'). So the descriptor above stays the
 * single minting site and this reads `key` off it — the
 * `MEDICATION_HIGHLIGHTS_FLAG_KEY` precedent.
 */
export const NEW_SESSION_LIST_FLAG_KEY = newSessionListFlag.key;

/**
 * Prefetches the session-by-id query on session-row click so the GET overlaps
 * the route transition instead of firing after the page mounts. The value is
 * also attached to `perf.scribe.session_open` tracking so the open-latency arms
 * can be compared in Datadog/RUM.
 */
export const sessionPrefetchFlag = defineFeatureFlag({
  key: 'release-scribe-enable-session-prefetch',
  defaultValue: false,
  description: 'Session-by-id prefetch on session-row click',
  builds: 'all',
});

/** THE KEY, PUBLISHED — same derivation and same reason as the one above. */
export const SESSION_PREFETCH_FLAG_KEY = sessionPrefetchFlag.key;

/**
 * The WEB arm of the Session Merge settings gate. Platform-split so the two
 * ramps are fully decoupled.
 */
export const sessionMergeWebFlag = defineFeatureFlag({
  key: 'release-apps-session-merge-web',
  defaultValue: false,
  description: 'Session Merge settings surface on web',
  builds: 'all',
});

/** The DESKTOP arm of the same gate. */
export const sessionMergeDesktopFlag = defineFeatureFlag({
  key: 'release-apps-session-merge-desktop',
  defaultValue: false,
  description: 'Session Merge settings surface on desktop',
  builds: 'all',
});

/**
 * Schedule Upload — PDF support: the modal and sidebar dropzone accept PDFs in
 * addition to CSV/PNG/JPEG. Gated separately because it depends on a backend
 * rollout.
 */
export const scheduleUploadPdfFlag = defineFeatureFlag({
  key: 'release-scribe-enable-schedule-upload-pdf',
  defaultValue: false,
  description: 'PDF support in the schedule upload flow',
  builds: 'all',
});

/**
 * Emergency kill switch for schedule-sidebar drag-and-drop. When enabled, the
 * sidebar restores the propagation stop that prevents react-dropzone from
 * accepting drag events; browse and paste uploads remain available.
 */
export const scheduleSidebarDragDropKillFlag = defineFeatureFlag({
  key: 'op-scribe-disable-schedule-sidebar-drag-drop',
  defaultValue: false,
  description: 'Kill switch for schedule-sidebar drag-and-drop',
  builds: 'all',
});

/**
 * Schedule Upload — context field: each draft session in the upload preview
 * shows an editable "Session context" textarea forwarded to the bulk-create
 * endpoint and stored on `Session.clinician_notes`.
 */
export const scheduleUploadContextFieldFlag = defineFeatureFlag({
  key: 'release-scribe-enable-schedule-upload-context-field',
  defaultValue: false,
  description: 'Session context field in the schedule upload preview',
  builds: 'all',
});

/**
 * UMBRELLA gate for the entire Scribe Canvas flexible-artefact experience:
 * type-into-sections, blank documents, the searchable "+ Create" menu and
 * dictation-as-a-canvas-artefact. Deliberately distinct from the backend
 * rollout flag `release-scribe-flexible-artefact-generation` so the two roll out
 * independently. Rollout/kill gate, NOT an experiment — no `useManualExposure`.
 */
export const scribeCanvasExperienceFlag = defineFeatureFlag({
  key: 'release-scribe-canvas-experience',
  defaultValue: false,
  description: 'Scribe Canvas flexible-artefact experience',
  builds: 'all',
});

/** THE KEY, PUBLISHED — same derivation and same reason as the two above. */
export const SCRIBE_CANVAS_EXPERIENCE_FLAG_KEY = scribeCanvasExperienceFlag.key;

/**
 * Previewing a Context tab attachment: on desktop, clicking a file chip opens
 * the original upload instead of doing nothing. A kill switch for a failing blob
 * fetch or a renderer crash on an unexpected format — OFF restores the previous
 * behaviour and the extracted text stays reachable either way.
 */
export const contextDocumentPreviewFlag = defineFeatureFlag({
  key: 'release-scribe-context-document-preview',
  defaultValue: false,
  description: 'Preview of a Context tab attachment',
  builds: 'all',
});

/** Text and Markdown Context-tab uploads, held behind a rollout kill switch. */
export const textContextDocumentUploadFlag = defineFeatureFlag({
  key: 'release-scribe-text-context-documents',
  defaultValue: false,
  description: 'Text and Markdown uploads in the Context tab',
  builds: 'all',
});

/** Failed context-file chips stay visible, behind a rollout kill switch. */
export const contextUploadFailedChipsFlag = defineFeatureFlag({
  key: 'release-scribe-context-upload-failed-chips',
  defaultValue: false,
  description: 'Failed context-file chips stay visible and deletable',
  builds: 'all',
});

/**
 * Umbrella gate for the "Start now for current sessions" experience: stacked
 * sub-labels, progressive date display, gutter checkboxes, the dedicated actions
 * area, Upcoming in split view, and individuals vs teams views.
 */
export const startNowFlag = defineFeatureFlag({
  key: 'release-scribe-start-now',
  defaultValue: false,
  description: 'Start now for current sessions',
  builds: 'all',
});

/**
 * UMBRELLA gate for the collapsed-Upcoming session-list experience. The single
 * derivation point (`collapsedUpcomingMode` in `use-session-list-query-state`)
 * ANDs this flag with the default date-DESC sort and every surface derives from
 * that one value, so OFF is byte-identical pre-feature behaviour with no half-on
 * state. Rollout/kill gate, NOT an experiment — no `useManualExposure`.
 */
export const collapsedUpcomingSessionsFlag = defineFeatureFlag({
  key: 'release-scribe-collapsed-upcoming-sessions',
  defaultValue: false,
  description: 'Collapsed-Upcoming session-list experience',
  builds: 'all',
});

/**
 * Umbrella rollout gate for the note-gen recovery experience: when note
 * generation fails but the transcript is fine, the app auto-retries and offers
 * regenerate instead of showing a transcript error. Every surface of that
 * experience gates behind this one flag so it ships all-on or all-off. Default
 * OFF ⇒ current behaviour is fully unchanged. `builds: 'all'` because the note
 * error overlay renders on the on-prem appliance too.
 * SCR-4406: the key rename was approved on 2026-10-06 with no alias. Its
 * historical never-deployed premise was contradicted by infra PR #2948's plan;
 * existing flag state and overrides require operator review before deletion.
 */
export const noteGenerationRecoveryFlag = defineFeatureFlag({
  key: 'release-scribe-note-generation-recovery',
  defaultValue: false,
  description: 'Note-gen recovery: auto-retry + regenerate over a safe transcript',
  builds: 'all',
});

/**
 * A FAILED consult note with a saved user edit shows that edit, not the error
 * overlay. Default OFF ships it dark. `builds: 'all'`: the overlay renders on-prem too.
 */
export const failedNoteRescueFlag = defineFeatureFlag({
  key: 'release-scribe-failed-note-rescue',
  defaultValue: false,
  description:
    'FAILED note with a saved edit shows the edit instead of the error overlay',
  builds: 'all',
});

export const sessionListScrollHighlightFlag = defineFeatureFlag({
  key: 'release-scribe-session-list-scroll-highlight',
  defaultValue: false,
  description: 'Session-list hover highlight driven by pointer hit-testing',
  builds: 'all',
});

/**
 * In split view, an unfiltered own-scope session list that settles empty
 * creates and opens a session instead of resting on the empty state. Default
 * OFF ships it dark.
 */
export const createSessionOnEmptyFlag = defineFeatureFlag({
  key: 'release-scribe-create-session-on-empty',
  defaultValue: false,
  description: 'Split view: empty own-scope session list creates and opens a session',
  builds: 'all',
});

/**
 * Session layout V2: visit-guidance surface items (including routine output)
 * render in a "Heidi suggests" card under the Session card, instead of the
 * Tasks card's Routines tab. Default OFF ships it dark. The app gate also
 * requires the Heidi Work umbrella (`useIsV2HeidiSuggestsEnabled`).
 */
export const v2HeidiSuggestsFlag = defineFeatureFlag({
  key: 'release-scribe-v2-heidi-suggests',
  defaultValue: false,
  description: 'Session V2: guidance surface items in a Heidi suggests card',
  builds: 'all',
});

/**
 * Post-recording warning when the transcript carried too little speech for its length.
 * Cloud-only: on-prem has no transcription service transcript to measure.
 */
export const poorAudioWarningFlag = defineFeatureFlag({
  key: 'release-scribe-poor-audio-warning',
  defaultValue: false,
  description: 'Post-recording poor-audio (low speech density) warning banner',
  builds: 'cloud-only',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off its descriptor.
//
// THE FORCE-ALL COLLAPSE, disclosed per getter rather than in aggregate. Three
// of the app-side bodies these replace carried a hand-rolled
// `process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true'` branch:
// `useIsScribeCanvasExperienceEnabled`, its non-React twin
// `isScribeCanvasExperienceEnabled`, and
// `useIsCollapsedUpcomingSessionsEnabled`. `useBooleanFlag` and `readFlag`
// return `forceEnableAll || value` from the boolean the app injects at
// `src/utils/runtime-wiring.ts` out of that same env var, so each branch
// COLLAPSES onto the door's own override rather than being dropped. The
// remaining getters GAIN that override, which a raw `useBooleanFlagValue`
// bypassed — the choke point the door exists for (design D8), and a dev-only
// knob. `flags.test.ts` asserts the ABSENCE of `process.env` here, so a later
// edit cannot reinstate a branch by hand.
// ---------------------------------------------------------------------------

/** The session-list primary action and its supporting toolbar layout. */
export const useIsSessionListPrimaryActionEnabled = (): boolean =>
  useBooleanFlag(
    sessionListPrimaryActionFlag.key,
    sessionListPrimaryActionFlag.defaultValue
  );

/**
 * Whole-scope status counts on the All Sessions filter.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsMultiScopeStatusCountsEnabled = (): boolean =>
  useBooleanFlag(multiScopeStatusCountsFlag.key, multiScopeStatusCountsFlag.defaultValue);

/** The new session list — table view and consolidated session lists. */
export const useIsNewSessionListEnabled = (): boolean =>
  // Lite: read from `useSessionsPanelLayout` / `useIsSessionPageV2Enabled`,
  // i.e. by most of the session page — see `useBooleanFlagLite`.
  useBooleanFlagLite(newSessionListFlag.key, newSessionListFlag.defaultValue);

/** Stable startup is an additional rollout gate beneath the new session list. */
export const useIsStableSessionSplitStartupEnabled = (): boolean =>
  useBooleanFlag(
    stableSessionSplitStartupFlag.key,
    stableSessionSplitStartupFlag.defaultValue
  );

/** Session-by-id prefetch on session-row click. */
export const useIsSessionPrefetchEnabled = (): boolean =>
  useBooleanFlag(sessionPrefetchFlag.key, sessionPrefetchFlag.defaultValue);

/**
 * The Session Merge settings surface and its one-time onboarding prompt. Gates
 * ONLY that surface — all other session-merge behaviour is ungated.
 *
 * Both platform reads happen unconditionally, so hook order is stable across
 * renders regardless of `isTauri()`.
 */
export const useIsSessionMergeEnabled = (): boolean => {
  const isWebEnabled = useBooleanFlag(
    sessionMergeWebFlag.key,
    sessionMergeWebFlag.defaultValue
  );
  const isDesktopEnabled = useBooleanFlag(
    sessionMergeDesktopFlag.key,
    sessionMergeDesktopFlag.defaultValue
  );
  return isTauri() ? isDesktopEnabled : isWebEnabled;
};

/** PDF support in the schedule upload flow. */
export const useIsScheduleUploadPdfEnabled = (): boolean =>
  useBooleanFlag(scheduleUploadPdfFlag.key, scheduleUploadPdfFlag.defaultValue);

/** The kill switch for schedule-sidebar drag-and-drop. */
export const useIsScheduleSidebarDragDropKilled = (): boolean =>
  useBooleanFlag(
    scheduleSidebarDragDropKillFlag.key,
    scheduleSidebarDragDropKillFlag.defaultValue
  );

/** The session-context field in the schedule upload preview. */
export const useIsScheduleUploadContextFieldEnabled = (): boolean =>
  useBooleanFlag(
    scheduleUploadContextFieldFlag.key,
    scheduleUploadContextFieldFlag.defaultValue
  );

/** The Scribe Canvas flexible-artefact experience. */
export const useIsScribeCanvasExperienceEnabled = (): boolean =>
  useBooleanFlag(scribeCanvasExperienceFlag.key, scribeCanvasExperienceFlag.defaultValue);

/**
 * The same flag for plain-JS call sites — TipTap extension factories and
 * markdown helpers that are not React components. Ruling 36 (b)'s fetch-time
 * shape: `readFlag` is the door's synchronous read over the same OpenFeature
 * domain client the app-side body resolved directly, so the resolution path is
 * unchanged.
 */
export const isScribeCanvasExperienceEnabled = (): boolean =>
  readFlag(scribeCanvasExperienceFlag.key, scribeCanvasExperienceFlag.defaultValue);

/** Preview of a Context tab attachment. */
export const useIsContextDocumentPreviewEnabled = (): boolean =>
  useBooleanFlag(contextDocumentPreviewFlag.key, contextDocumentPreviewFlag.defaultValue);

/** Text and Markdown Context-tab uploads. */
export const useIsTextContextDocumentUploadEnabled = (): boolean =>
  useBooleanFlag(
    textContextDocumentUploadFlag.key,
    textContextDocumentUploadFlag.defaultValue
  );

/** Failed context-file chips stay visible and deletable. */
export const useIsContextUploadFailedChipsEnabled = (): boolean =>
  useBooleanFlag(
    contextUploadFailedChipsFlag.key,
    contextUploadFailedChipsFlag.defaultValue
  );

/** The "Start now for current sessions" experience. */
export const useIsStartNowEnabled = (): boolean =>
  useBooleanFlag(startNowFlag.key, startNowFlag.defaultValue);

/** The collapsed-Upcoming session-list experience. */
export const useIsCollapsedUpcomingSessionsEnabled = (): boolean =>
  useBooleanFlag(
    collapsedUpcomingSessionsFlag.key,
    collapsedUpcomingSessionsFlag.defaultValue
  );

/** The note-gen recovery experience over a safe transcript. */
export const useIsNoteGenerationRecoveryEnabled = (): boolean =>
  useBooleanFlag(noteGenerationRecoveryFlag.key, noteGenerationRecoveryFlag.defaultValue);

/** The same flag for plain-JS call sites — the stream error path is not a component. */
export const isNoteGenerationRecoveryEnabled = (): boolean =>
  readFlag(noteGenerationRecoveryFlag.key, noteGenerationRecoveryFlag.defaultValue);

/** Show a FAILED note's saved edit instead of the error overlay. */
export const useIsFailedNoteRescueEnabled = (): boolean =>
  useBooleanFlag(failedNoteRescueFlag.key, failedNoteRescueFlag.defaultValue);

/**
 * Pointer-driven session-list hover highlight, a workaround for a stale
 * `:hover` in WebKit. Platform scoping is TARGETING-ONLY — the flag alone
 * controls the feature, so a Flagsmith rule is what narrows it to the intended
 * cohort and there is no code-level platform guard behind it.
 */
export const useIsSessionListScrollHighlightEnabled = (): boolean =>
  useBooleanFlag(
    sessionListScrollHighlightFlag.key,
    sessionListScrollHighlightFlag.defaultValue
  );

/** Create a session when the unfiltered own-scope list settles empty. */
export const useIsCreateSessionOnEmptyEnabled = (): boolean =>
  useBooleanFlag(createSessionOnEmptyFlag.key, createSessionOnEmptyFlag.defaultValue);

/** The V2 "Heidi suggests" card for visit-guidance surface items. */
export const useIsV2HeidiSuggestsFlagEnabled = (): boolean =>
  useBooleanFlag(v2HeidiSuggestsFlag.key, v2HeidiSuggestsFlag.defaultValue);

/** The post-recording poor-audio warning banner. */
export const useIsPoorAudioWarningEnabled = (): boolean =>
  useBooleanFlag(poorAudioWarningFlag.key, poorAudioWarningFlag.defaultValue);
